import { defineRequest, defineRequestWithParam } from "../axios"
import type {
    AddTagAssignerPayload,
    CompareTagAssignersPayload,
    CompareTagsPayload,
    CreatedTagPayload,
    EditTagPayload,
    ExportTagNameListPayload,
    PatchTagAssignerPayload,
    TagAssignerComparisonResponse,
    TagAssignerCopyResponse,
    TagAssignerItem,
    TagAssignerListResponse,
    TagAssignerMemberListResponse,
    TagAssignerStatusListResponse,
    TagAssignerSummaryResponse,
    TagAssignerTypeListResponse,
    TagComparisonRequestResponse,
    TagComparisonResultResponse,
    TagItem,
    TagListResponse,
    TagMemberListResponse,
    TagStatusListResponse,
    TagSummaryListResponse,
    UpdateTagAssignerPayload,
} from "./types"

// 取得可用的標籤列表
export const getTagList = defineRequest<TagListResponse>({
    url: "/tags/",
    method: "get",
})

// 取得標籤狀態
export const getTagStatus = defineRequest<TagStatusListResponse>({
    url: "/tags/status/",
    method: "get",
})

// 新增一個標籤
export const createdTag = defineRequest<TagItem, CreatedTagPayload>({
    url: "/tags/",
    method: "post",
})

// 取得篩選貼標列表
export const getTagAssignerList = defineRequest<TagAssignerListResponse>({
    url: "/tags/assigners/",
    method: "get",
})

// 新增篩選貼標資料
export const addTagAssigner = defineRequest<TagAssignerItem, AddTagAssignerPayload>({
    url: "/tags/assigners/",
    method: "post",
})

// 複製篩選貼標資料
export const copyTagAssigner = defineRequestWithParam<TagAssignerCopyResponse>({
    url: (taggingUuid: string) => `/tags/assigners/${taggingUuid}/copy/`,
    method: "post",
})

// 取得單一篩選貼標資料
export const getTagAssigner = defineRequestWithParam<TagAssignerItem>({
    url: (taggingUuid: string) => `/tags/assigners/${taggingUuid}/`,
    method: "get",
})

// 更新單一篩選貼標資料
export const updateTagAssigner = defineRequestWithParam<TagAssignerItem, UpdateTagAssignerPayload>({
    url: (taggingUuid: string) => `/tags/assigners/${taggingUuid}/`,
    method: "put",
})

// 局部更新單一篩選貼標資料
export const patchTagAssigner = defineRequestWithParam<TagAssignerItem, PatchTagAssignerPayload>({
    url: (taggingUuid: string) => `/tags/assigners/${taggingUuid}/`,
    method: "patch",
})

// 停用單一篩選貼標資料
export const disableTagAssigner = defineRequestWithParam<void>({
    url: (taggingUuid: string) => `/tags/assigners/${taggingUuid}/disable/`,
    method: "post",
})

// 刪除單一篩選貼標資料
export const deleteTagAssigner = defineRequestWithParam<void>({
    url: (taggingUuid: string) => `/tags/assigners/${taggingUuid}/`,
    method: "delete",
})

// 新增篩選貼標
export const postTagAssignerAssign = defineRequestWithParam<void>({
    url: (taggingUuid: string) => `/tags/assigners/${taggingUuid}/assign/`,
    method: "post",
})

// 比較篩選貼標
export const compareTagAssigners = defineRequest<TagAssignerComparisonResponse, CompareTagAssignersPayload>({
    url: "/tags/assigners/compare/",
    method: "post",
})

// 取得篩選貼標名單
export const getTagAssignerMembers = defineRequestWithParam<TagAssignerMemberListResponse>({
    url: (taggingUuid: string) => `/tags/assigners/${taggingUuid}/members/`,
    method: "get",
})

// 取得篩選貼標分析資料
export const getTagAssignerSummary = defineRequestWithParam<TagAssignerSummaryResponse>({
    url: (taggingUuid: string) => `/tags/assigners/${taggingUuid}/summary/`,
    method: "get",
})

// 取得篩選貼標狀態
export const getTagAssignerStatus = defineRequest<TagAssignerStatusListResponse>({
    url: "/tags/assigners/status/",
    method: "get",
})

// 取得篩選貼標類型
export const getTagAssignerType = defineRequest<TagAssignerTypeListResponse>({
    url: "/tags/assigners/type/",
    method: "get",
})

// 下載貼標名單
export const downloadTagAssignerMemberList = defineRequestWithParam<void>({
    url: (taggingUuid: string) => `/tags/assigners/${taggingUuid}/export/`,
    method: "post",
})

// 比較多筆標籤
export const compareTags = defineRequest<TagComparisonRequestResponse, CompareTagsPayload>({
    url: "/tags/compare/",
    method: "post",
})

// 取得標籤比較結果
export const getTagsComparison = defineRequestWithParam<TagComparisonResultResponse>({
    url: (tagComparisonUuid: string) => `/tags/comparison/${tagComparisonUuid}/`,
    method: "get",
})

// 取得標籤資訊
export const getTagInfo = defineRequestWithParam<TagItem>({
    url: (tagUuid: string) => `/tags/${tagUuid}/`,
    method: "get",
})

// 編輯標籤名稱
export const editTag = defineRequestWithParam<TagItem, EditTagPayload>({
    url: (tagUuid: string) => `/tags/${tagUuid}/`,
    method: "put",
})

// 刪除標籤
export const deleteTag = defineRequestWithParam<void>({
    url: (tagUuid: string) => `/tags/${tagUuid}/`,
    method: "delete",
})

// 停用標籤
export const disableTag = defineRequestWithParam<void>({
    url: (tagUuid: string) => `/tags/${tagUuid}/disable/`,
    method: "post",
})

// 取得標籤會員列表
export const getTagMemberList = defineRequestWithParam<TagMemberListResponse>({
    url: (tagUuid: string) => `/tags/${tagUuid}/members/`,
    method: "get",
})

// 取得標籤分析資料
export const getTagSummary = defineRequestWithParam<TagSummaryListResponse>({
    url: (tagUuid: string) => `/tags/${tagUuid}/summary/`,
    method: "get",
})

// 匯出標籤會員名單
export const exportTagNameList = defineRequestWithParam<void, ExportTagNameListPayload>({
    url: (tagUuid: string) => `/tags/${tagUuid}/export/`,
    method: "post",
})
